import React from 'react';
import classNames from 'classnames';
import { isString } from 'lodash';
import { getImageUrl } from '../../../../services';
import { isIos } from '../../../../services/detect-platform';
import { createHashtagPath, createHashtagHref } from '../../../../services/search-page-url';
import { generalLinkSettings } from '../../constants/linkSettings';
import { RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY } from '../../constants/messages';
import { viewerCustomStyleFn, viewerCustomBackgroundStyleFn, styleSelectionPredicate, getTextColorSchema, } from '../../services/text-color-utils';
import styles from './ricos-viewer-wrapper.scss';
const MAX_PINTEREST_IMAGE_SIZE = 5000;
class RicosViewer extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            error: false,
        };
        this.getImagePluginConfig = () => ({
            encoding: 'AVIF',
            imageProps: (src) => this.props.pageUrl
                ? {
                    'data-pin-url': this.props.pageUrl,
                    'data-pin-media': isString(src)
                        ? src
                        : getImageUrl({
                            image: src,
                            maxWidth: MAX_PINTEREST_IMAGE_SIZE,
                            maxHeight: MAX_PINTEREST_IMAGE_SIZE,
                        }),
                }
                : {},
        });
        this.getGalleryPluginConfig = () => {
            const { pageUrl } = this.props;
            if (!pageUrl) {
                return {};
            }
            return {
                imageProps: () => ({ 'data-pin-url': pageUrl }),
            };
        };
        this.getTextColorPluginConfig = () => {
            const { appSettings, isHexColor } = this.props;
            const colorScheme = getTextColorSchema({ appSettings });
            return {
                customStyleFn: viewerCustomStyleFn(colorScheme, { isHexColor }),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                    isHexColor,
                }),
            };
        };
        this.getTextHighlightPluginConfig = () => {
            const { appSettings, isHexColor } = this.props;
            const colorScheme = getTextColorSchema({ appSettings });
            return {
                customStyleFn: viewerCustomBackgroundStyleFn(colorScheme, { isHexColor }),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                    isHexColor,
                }),
            };
        };
        this.getLinkPreviewPluginConfig = () => {
            return {
                enableEmbed: true,
                enableLinkPreview: true,
                enableEnterToConvertLinks: false,
            };
        };
        this.getHashtagPluginConfig = () => {
            const { hashtagNavigate, sectionUrl } = this.props;
            return {
                onClick: (event, text) => {
                    event.preventDefault();
                    event.stopPropagation();
                    hashtagNavigate(createHashtagPath(text, {
                        newPath: this.props.isHashtagPageAsFeedPageEnabled,
                    }));
                },
                createHref: (text) => createHashtagHref(text, sectionUrl, {
                    newPath: this.props.isHashtagPageAsFeedPageEnabled,
                }),
            };
        };
        this.getActionButtonPluginConfig = () => ({ onClick: this.props.onButtonClick });
        this.getFileDownloadBIProps = (file, success) => {
            const { postId, isDemoPost } = this.props;
            return {
                file_ext: file.type,
                file_id: file.id,
                file_size: file.size,
                mime_type: file.mimeType,
                post_stable_id: postId,
                is_demo: isDemoPost,
                origin: 'post',
                success,
            };
        };
        this.getFilePluginConfig = () => {
            const { postId, bi, actions } = this.props;
            return {
                resolveFileUrl: (file) => {
                    isIos() && actions.showMessage(RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY);
                    return actions
                        .requestFileDownloadUrl(postId, file.path, file.id, file.mimeType)
                        .then(({ url }) => {
                        bi.fileDownloaded(this.getFileDownloadBIProps(file, true));
                        return url;
                    })
                        .catch(() => bi.fileDownloaded(this.getFileDownloadBIProps(file, false)));
                },
            };
        };
        this.getPollSettings = () => {
            const onBeforeVote = ({ voteRole }) => {
                const { currentUser, actions, isBusinessManager } = this.props;
                return new Promise((resolve, reject) => {
                    if (isBusinessManager) {
                        return reject();
                    }
                    if (currentUser || voteRole === 'ALL') {
                        return resolve();
                    }
                    actions.requestLogin().then(resolve, reject);
                });
            };
            return { onBeforeVote };
        };
        this.getPlugins = () => {
            const plugins = [
                this.props.pluginAudio(),
                this.props.pluginCodeBlock(),
                this.props.pluginDivider(),
                this.props.pluginLinkButton(),
                this.props.pluginGallery(this.getGalleryPluginConfig()),
                this.props.pluginGiphy(),
                this.props.pluginHashtag(this.getHashtagPluginConfig()),
                this.props.pluginHtml(),
                this.props.pluginImage(this.getImagePluginConfig()),
                this.props.pluginLineSpacing(),
                this.props.pluginIndent(),
                this.props.pluginLink(),
                this.props.pluginTextColor(this.getTextColorPluginConfig()),
                this.props.pluginTextHighlight(this.getTextHighlightPluginConfig()),
                this.props.pluginLinkPreview(this.getLinkPreviewPluginConfig()),
                this.props.pluginVideo(),
                this.props.pluginFileUpload(this.getFilePluginConfig()),
                this.props.pluginActionButton(this.getActionButtonPluginConfig()),
                this.props.pluginVerticalEmbed(),
                this.props.pluginTable(),
                this.props.pluginCollapsibleList(),
                ...(this.props.pluginPoll ? [this.props.pluginPoll(this.getPollSettings())] : []),
                ...(this.props.pluginShape ? [this.props.pluginShape()] : []),
                ...(this.props.pluginSmartBlock ? [this.props.pluginSmartBlock()] : []),
                ...(this.props.pluginToc ? [this.props.pluginToc()] : []),
            ];
            return plugins;
        };
        this.onError = (error) => {
            if (!this.state.error) {
                this.setState({ error });
            }
        };
        // TODO: remove when ricos supports plain object
        this.wixExperiments = {
            all: () => props.experiments,
        };
    }
    render() {
        if (this.state.error) {
            return this.props.fallback?.() ?? null;
        }
        const { WixRicosViewer, content, rceVersion, theme, section, isBusinessManager, isSeo, isMobile, locale, isPostPageBundle, instance, postId, setViewerRef, baseUrl, iframeSandboxDomain, visitorId, } = this.props;
        const plugins = this.getPlugins();
        const className = classNames(styles.container);
        return (React.createElement("div", { className: styles.relativeContainer },
            React.createElement("div", { ref: setViewerRef, className: className, "data-rce-version": rceVersion },
                React.createElement(WixRicosViewer, { plugins: plugins, content: content, theme: theme, locale: locale, instance: instance, onError: this.onError, isMobile: isMobile, seoSettings: isSeo, generalLinkSettings: this.props.generalLinkSettings ?? generalLinkSettings, wixExperiments: this.wixExperiments, addAnchors: true, mediaSettings: this.props.mediaSettings, baseUrl: baseUrl, iframeSandboxDomain: iframeSandboxDomain, biSettings: {
                        consumer: 'Blog',
                        contentId: postId,
                        platform: isBusinessManager ? 'BM' : 'Livesite',
                        usage: isPostPageBundle ? 'Post' : section,
                        defaultParams: {
                            visitorId,
                        },
                    } }))));
    }
}
RicosViewer.defaultProps = {
    isBusinessManager: false,
    fallback: () => 'Content cannot be displayed',
};
export default RicosViewer;
//# sourceMappingURL=ricos-viewer.js.map